import React, { createContext, useState, useCallback, useEffect, useRef } from 'react'
import { UserManager, WebStorageStateStore } from 'oidc-client-ts'
import { setTokenProvider } from '../api/client.js'

const cognitoRegion = import.meta.env.VITE_COGNITO_REGION || 'eu-west-1'
const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID || ''
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID || ''
const authDomain = import.meta.env.VITE_COGNITO_AUTH_DOMAIN || ''
const appUrl = import.meta.env.VITE_APP_URL || window.location.origin

const issuer = `https://cognito-idp.${cognitoRegion}.amazonaws.com/${userPoolId}`

let userManager = null
try {
  if (userPoolId && clientId && authDomain) {
    userManager = new UserManager({
      authority: issuer,
      client_id: clientId,
      redirect_uri: `${appUrl}/callback`,
      post_logout_redirect_uri: appUrl,
      response_type: 'code',
      scope: 'openid email profile aws.cognito.signin.user.admin',
      automaticSilentRenew: true,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      metadata: {
        issuer,
        authorization_endpoint: `https://${authDomain}/oauth2/authorize`,
        token_endpoint: `https://${authDomain}/oauth2/token`,
        userinfo_endpoint: `https://${authDomain}/oauth2/userInfo`,
        end_session_endpoint: `https://${authDomain}/logout`,
      },
    })
  }
} catch (_) {
  // ignore in test environments
}

const logoutChannel = new BroadcastChannel('auth')

// If silent renew fails and the access token expires, clear local session and
// send the user through Cognito logout so they land on a clean sign-in state
// instead of issuing requests with a stale token (which produces
// UnauthorizedException noise in Sentry).
if (userManager) {
  userManager.events.addAccessTokenExpired(async () => {
    try {
      await userManager.removeUser()
    } catch (_) {
      // ignore
    }
    logoutChannel.postMessage('signout')
    const logoutUrl = `https://${authDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(appUrl)}`
    window.location.href = logoutUrl
  })
}

export const AuthContext = createContext(null)

export { userManager }

function parseUser(oidcUser) {
  if (!oidcUser) return null
  const { profile, id_token, access_token } = oidcUser
  return {
    sub: profile.sub,
    email: profile.email,
    name: profile.name || profile.email,
    // Immutable org slug injected by Cognito's custom:org attribute. Single
    // org per user today — present so UI can reflect scope without a fetch.
    org: profile['custom:org'] || null,
    idToken: id_token,
    accessToken: access_token,
  }
}

export { parseUser }

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const userRef = useRef(user)
  userRef.current = user

  const getAccessToken = useCallback(async () => {
    if (!userManager) return userRef.current?.idToken || null
    try {
      const oidcUser = await userManager.getUser()
      if (oidcUser && !oidcUser.expired) return oidcUser.id_token
      // Token is expired — try a silent renew before giving up so callers
      // don't fire requests with a stale token.
      if (oidcUser && oidcUser.expired) {
        const renewed = await userManager.signinSilent().catch(() => null)
        if (renewed && !renewed.expired) return renewed.id_token
      }
    } catch (_) {
      // fall through
    }
    return null
  }, [])

  useEffect(() => {
    setTokenProvider(getAccessToken)
  }, [getAccessToken])

  // Restore session on mount
  useEffect(() => {
    if (!userManager) { setLoading(false); return }
    userManager.getUser().then((oidcUser) => {
      if (oidcUser && !oidcUser.expired) {
        setUser(parseUser(oidcUser))
      }
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [])

  // ``loginHint`` is forwarded to Cognito Managed Login as the standard
  // OIDC ``login_hint`` parameter. Cognito honours it by pre-filling
  // (and typically skipping) the email screen — avoiding a duplicate
  // email prompt on top of the one we already gathered for SSO discovery.
  // Guarded as a non-empty string because some callers wire ``signIn``
  // directly to ``onClick``, which would pass a SyntheticEvent.
  const signIn = useCallback((loginHint) => {
    if (!userManager) return
    const opts = typeof loginHint === 'string' && loginHint.length > 0
      ? { extraQueryParams: { login_hint: loginHint } }
      : undefined
    userManager.signinRedirect(opts)
  }, [])

  // Federated sign-in via the Cognito Hosted UI. The ``identity_provider``
  // query parameter tells Hosted UI to bypass the native login form and
  // redirect straight to the named OIDC provider (e.g. ``entra-acme``).
  const signInWithIdp = useCallback((providerName) => {
    if (!userManager || !providerName) return
    userManager.signinRedirect({ extraQueryParams: { identity_provider: providerName } })
  }, [])

  const signOut = useCallback(async () => {
    if (!userManager) { setUser(null); return }
    await userManager.removeUser()
    setUser(null)
    logoutChannel.postMessage('signout')
    // Redirect to Cognito logout endpoint to clear server-side session
    const logoutUrl = `https://${authDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(appUrl)}`
    window.location.href = logoutUrl
  }, [])

  useEffect(() => {
    logoutChannel.onmessage = (e) => { if (e.data === 'signout') setUser(null) }
  }, [])

  // Called by CallbackHandler after successful token exchange
  const handleCallback = useCallback(async () => {
    if (!userManager) throw new Error('Auth not configured')
    const oidcUser = await userManager.signinRedirectCallback()
    setUser(parseUser(oidcUser))
    return oidcUser
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signInWithIdp, signOut, handleCallback }}>
      {children}
    </AuthContext.Provider>
  )
}
